import React, { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { Package, Plus, Trash2, Settings, Calculator, Upload, FileSpreadsheet, ArrowUp, ArrowDown, GripVertical, Layers, Sigma } from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import CurrencyInput from "../resultados/CurrencyInput";
import * as XLSX from "xlsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  TIPO_LINHA,
  getTipoLinha,
  extrairNumero,
  aplicarColunasCalculadas,
  somarColunaAteSubtotal,
  calcularTotalGeralItens,
  ultimoGrupoIdDeCabecalho,
  ultimoGrupoIdAbertoParaNovosItens,
  contarGrupos,
  renumerarLinhasDados,
  recalcularLinhasDadosCalculoAutomatico,
  itensLinhasDadosCalculoDivergem,
  resolverIdsCalculoAutomaticoPreco,
} from "@/utils/propostaItensCalculations";
import { valorPorExtenso, textoValorTotalPdf } from "@/lib/valorExtenso";

/** Largura mínima por tipo/id para não truncar moeda e números na grelha. */
function classeLarguraCelulaItemProposta(coluna) {
  if (coluna?.largura && String(coluna.largura).trim().length > 2) {
    return coluna.largura;
  }
  const t = coluna?.tipo;
  if (t === "valor") return "min-w-[152px] w-[1%] whitespace-nowrap";
  if (t === "numero") return "min-w-[88px] w-[1%] whitespace-nowrap";
  if (t === "texto_longo") return "min-w-[240px] max-w-xl";
  if (coluna?.id === "grupo" || coluna?.id === "subgrupo") {
    return "w-24 min-w-[5.5rem] max-w-[7rem]";
  }
  return "min-w-[100px]";
}

function ColunaSortableItem({ coluna, index, onChangeColuna, onRemoveColuna, colunasLength, colunaEditavel }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: coluna.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Card ref={setNodeRef} style={style} className={`border-2 border-slate-200 ${isDragging ? "shadow-lg" : ""}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <div
            {...attributes}
            {...listeners}
            className="mt-6 cursor-grab active:cursor-grabbing hover:bg-slate-100 p-1 rounded flex-shrink-0"
            title="Arrastar para reordenar"
          >
            <GripVertical className="w-5 h-5 text-slate-400" />
          </div>
          <div className="flex-1 grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Nome da Coluna</Label>
              <Input
                value={coluna.nome}
                onChange={(e) => onChangeColuna(index, "nome", e.target.value)}
                placeholder="Ex: Descrição"
              />
            </div>
            <div className="space-y-2">
              <Label>Tipo de Dado</Label>
              <select
                value={coluna.tipo}
                onChange={(e) => onChangeColuna(index, "tipo", e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white"
              >
                <option value="texto">Texto</option>
                <option value="texto_longo">Texto Longo</option>
                <option value="numero">Número</option>
                <option value="valor">Valor (R$)</option>
                <option value="calculada">Calculada (fórmula)</option>
              </select>
            </div>
            {coluna.tipo === "calculada" && (
              <div className="col-span-2 space-y-2">
                <Label>Fórmula — use {"{"}id_da_coluna{"}"}</Label>
                <Input
                  value={coluna.formula || ""}
                  onChange={(e) => onChangeColuna(index, "formula", e.target.value)}
                  placeholder="Ex: {quantidade}*{valor_unitario}"
                  className="font-mono text-sm"
                />
                <p className="text-xs text-slate-500">
                  Referencie colunas pelos IDs (ex.: quantidade, valor_unitario, col_2). Operações: + − * / ( ).
                </p>
              </div>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onRemoveColuna(index)}
            className="hover:bg-red-100 hover:text-red-700 flex-shrink-0 mt-6"
            disabled={colunasLength <= 3 || colunaEditavel === false}
            title={colunasLength <= 3 ? "É necessário ter ao menos 3 colunas" : colunaEditavel === false ? "Coluna obrigatória" : "Remover coluna"}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function ItemsProposta({
  proposta,
  onChange,
  onSaveImmediate,
  onImportacaoPlanilhaConcluida,
}) {
  const [dialogColunasAberto, setDialogColunasAberto] = useState(false);
  const [dialogImportacaoAberto, setDialogImportacaoAberto] = useState(false);
  const [calculoAutomatico, setCalculoAutomatico] = useState(true);
  const [importando, setImportando] = useState(false);
  const fileInputRef = useRef(null);
  
  // Estado para campos extras (descrição + valor + extenso)
  const [novoCampoExtra, setNovoCampoExtra] = useState({ descricao: "", valor: 0 });
  const [mesesDivisaoExtra, setMesesDivisaoExtra] = useState("");
  const camposExtras = proposta.campos_extras || [];
  
  // Colunas padrão para proposta
  const colunasIniciais = [
    { id: "item", nome: "Item", tipo: "numero", largura: "w-16", visivel: true, editavel: false },
    { id: "descricao", nome: "Descrição", tipo: "texto_longo", largura: "min-w-[400px]", visivel: true, editavel: true },
    { id: "unidade", nome: "Unidade", tipo: "texto", largura: "w-24", visivel: true, editavel: true },
    { id: "quantidade", nome: "Quantidade", tipo: "numero", largura: "w-32", visivel: true, editavel: true },
    { id: "valor_unitario", nome: "Valor Unit.", tipo: "valor", largura: "min-w-[152px] whitespace-nowrap", visivel: true, editavel: true }
  ];

  const [colunas, setColunas] = useState(() => {
    if (proposta.colunas_itens && proposta.colunas_itens.length > 0) {
      return proposta.colunas_itens;
    }
    return colunasIniciais;
  });

  // Sincronizar colunas quando proposta.colunas_itens mudar (ex: após importação)
  useEffect(() => {
    if (proposta.colunas_itens && proposta.colunas_itens.length > 0) {
      setColunas(proposta.colunas_itens);
    }
  }, [proposta.colunas_itens]);

  const itens = proposta.itens || [];

  const formatarValor = (valor) => {
    if (valor === null || valor === undefined) return '-';
    
    let numValor = valor;
    if (typeof valor === 'string') {
      numValor = valor
        .replace(/R\$/g, '')
        .replace(/\s/g, '')
        .replace(/\./g, '')
        .replace(',', '.');
      numValor = parseFloat(numValor);
    } else {
      numValor = parseFloat(valor);
    }
    
    if (isNaN(numValor)) return '-';
    
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(numValor);
  };

  const calcularTotais = () => {
    const totalGeral = calcularTotalGeralItens(itens, colunas);
    return { totalGeral };
  };

  const totais = calcularTotais();

  // Com cálculo automático: ao carregar itens (ex.: editar proposta) alinhar valor_total e colunas calculadas
  useEffect(() => {
    if (!calculoAutomatico || !itens.length) return;
    const novosItens = recalcularLinhasDadosCalculoAutomatico(itens, colunas);
    if (!itensLinhasDadosCalculoDivergem(itens, novosItens, colunas)) return;
    onChange((prev) => (prev ? { ...prev, itens: novosItens } : prev));
  }, [calculoAutomatico, itens, colunas, onChange]);

  // Texto do PDF (valor por extenso) sempre sincronizado com o valor total efetivo
  useEffect(() => {
    const v = calculoAutomatico
      ? totais.totalGeral
      : Number(proposta.valor_total_proposta ?? 0);
    const texto = textoValorTotalPdf(v);
    onChange((prev) => {
      if (!prev || prev.texto_valor_total === texto) return prev;
      return { ...prev, texto_valor_total: texto };
    });
  }, [calculoAutomatico, totais.totalGeral, proposta.valor_total_proposta, onChange]);

  // Atualiza o valor total da proposta quando os itens mudarem
  useEffect(() => {
    if (!calculoAutomatico) return;
    const totalGeral = totais.totalGeral;
    onChange((prev) => {
      if (!prev || prev.valor_total_proposta === totalGeral) return prev;
      return { ...prev, valor_total_proposta: totalGeral };
    });
  }, [itens.length, calculoAutomatico, totais.totalGeral, onChange]);

  const handleAddItem = () => {
    const gid = ultimoGrupoIdAbertoParaNovosItens(itens);
    const novoItem = {
      tipo_linha: TIPO_LINHA.DADOS,
      ...(gid ? { grupo_id: gid } : {}),
      item: 0,
    };
    colunas.forEach((col) => {
      if (col.id === "item" || col.tipo === "calculada") return;
      novoItem[col.id] = col.tipo === "numero" || col.tipo === "valor" ? 0 : "";
    });
    let novosItens = [...itens, novoItem];
    novosItens = renumerarLinhasDados(novosItens);
    const lastIdx = novosItens.length - 1;
    novosItens[lastIdx] = aplicarColunasCalculadas(novosItens[lastIdx], colunas);
    onChange({ ...proposta, itens: novosItens });
  };

  const handleAddGrupo = () => {
    const n = contarGrupos(itens) + 1;
    const gid = `grupo_${Date.now()}`;
    const header = {
      tipo_linha: TIPO_LINHA.CABECALHO_GRUPO,
      grupo_id: gid,
      titulo_grupo: `ITEM ${n}`,
    };
    onChange({ ...proposta, itens: [...itens, header] });
  };

  const handleAddSubtotal = () => {
    const gid = ultimoGrupoIdDeCabecalho(itens);
    if (!gid) {
      toast.error("Adicione um grupo (cabeçalho) antes do subtotal.");
      return;
    }
    const sub = { tipo_linha: TIPO_LINHA.SUBTOTAL, grupo_id: gid };
    onChange({ ...proposta, itens: [...itens, sub] });
  };

  const handleChangeTituloGrupo = (index, value) => {
    const novosItens = [...itens];
    novosItens[index] = { ...novosItens[index], titulo_grupo: value };
    onChange({ ...proposta, itens: novosItens });
  };

  const handleRemoveItem = (index) => {
    const novosItens = renumerarLinhasDados(itens.filter((_, i) => i !== index));
    onChange({ ...proposta, itens: novosItens });
  };

  const handleChangeItem = (index, field, value) => {
    const row = itens[index];
    if (getTipoLinha(row) !== TIPO_LINHA.DADOS) return;

    const novosItens = [...itens];
    const coluna = colunas.find((c) => c.id === field);
    if (coluna?.tipo === "calculada") return;

    let valorProcessado = value;
    if (coluna?.tipo === "numero") {
      valorProcessado = parseFloat(value);
      if (isNaN(valorProcessado)) {
        valorProcessado = 0;
      }
    } else if (coluna?.tipo === "valor") {
      valorProcessado = extrairNumero(value);
    }

    novosItens[index] = {
      ...novosItens[index],
      [field]: valorProcessado,
    };

    const { idQtd, idVu, idVt } = resolverIdsCalculoAutomaticoPreco(colunas);
    if (
      calculoAutomatico &&
      idVu !== idVt &&
      (field === "quantidade" ||
        field === "valor_unitario" ||
        field === idQtd ||
        field === idVu)
    ) {
      const valorUnit =
        field === idVu || field === "valor_unitario"
          ? valorProcessado
          : extrairNumero(novosItens[index][idVu]);
      const qtd =
        field === idQtd || field === "quantidade"
          ? valorProcessado
          : extrairNumero(novosItens[index][idQtd]);
      const totalLinha = valorUnit * qtd;
      if (idVt) novosItens[index][idVt] = totalLinha;
      novosItens[index].valor_total = totalLinha;
      if (idQtd && idQtd !== "quantidade") novosItens[index].quantidade = qtd;
      if (idVu && idVu !== "valor_unitario") novosItens[index].valor_unitario = valorUnit;
    }

    novosItens[index] = aplicarColunasCalculadas(novosItens[index], colunas);
    onChange({ ...proposta, itens: novosItens });
  };

  const handleChangeTotal = (index, valorTotal) => {
    if (getTipoLinha(itens[index]) !== TIPO_LINHA.DADOS) return;
    const novosItens = [...itens];
    const item = novosItens[index];
    const total = extrairNumero(valorTotal);
    const { idQtd, idVu, idVt } = resolverIdsCalculoAutomaticoPreco(colunas);
    const quantidade = idQtd ? extrairNumero(item[idQtd]) : extrairNumero(item.quantidade);

    if (total === 0) {
      const patch = { ...item, valor_total: total };
      if (idVt && idVt !== idVu) patch[idVt] = total;
      novosItens[index] = aplicarColunasCalculadas(patch, colunas);
      onChange({ ...proposta, itens: novosItens });
      return;
    }

    const novoValorUnitario = quantidade > 0 ? total / quantidade : total;

    const patch = {
      ...item,
      valor_unitario: novoValorUnitario,
      valor_total: total,
    };
    if (idVu) patch[idVu] = novoValorUnitario;
    if (idVt && idVt !== idVu) patch[idVt] = total;

    novosItens[index] = aplicarColunasCalculadas(patch, colunas);

    onChange({ ...proposta, itens: novosItens });
  };

  const handleAddColuna = () => {
    const novaColuna = {
      id: `coluna_${Date.now()}`,
      nome: "Nova Coluna",
      tipo: "texto",
      largura: "w-32",
      visivel: true,
      editavel: true
    };
    setColunas([...colunas, novaColuna]);
  };

  const handleRemoveColuna = (index) => {
    const novasColunas = colunas.filter((_, i) => i !== index);
    setColunas(novasColunas);
  };

  const handleChangeColuna = (index, field, value) => {
    const novasColunas = [...colunas];
    if (field === "tipo" && value === "calculada" && !novasColunas[index].formula?.trim()) {
      novasColunas[index] = {
        ...novasColunas[index],
        tipo: value,
        formula: "{quantidade}*{valor_unitario}",
      };
    } else {
      novasColunas[index] = {
        ...novasColunas[index],
        [field]: value,
      };
    }
    setColunas(novasColunas);
  };

  const handleSalvarColunas = () => {
    const novosItens = itens.map((row) => {
      if (getTipoLinha(row) !== TIPO_LINHA.DADOS) return row;
      return aplicarColunasCalculadas(row, colunas);
    });
    onChange({ ...proposta, colunas_itens: colunas, itens: novosItens });
    setDialogColunasAberto(false);
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEndColunas = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setColunas((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleAplicarValorMensalExtra = () => {
    const meses = parseInt(String(mesesDivisaoExtra).trim(), 10);
    if (!Number.isFinite(meses) || meses < 1) {
      toast.error("Informe a quantidade de meses (número inteiro ≥ 1).");
      return;
    }
    const base = calculoAutomatico
      ? totais.totalGeral
      : Number(proposta.valor_total_proposta ?? 0);
    if (base <= 0) {
      toast.error("O valor total da proposta deve ser maior que zero.");
      return;
    }
    const valorMensal = Math.round((base / meses) * 100) / 100;
    setNovoCampoExtra({ ...novoCampoExtra, valor: valorMensal });
    toast.success("Valor mensal aplicado ao campo R$.");
  };

  const handleAddCampoExtra = () => {
    if (!novoCampoExtra.descricao.trim()) {
      toast.error("Informe a descrição");
      return;
    }
    const novosCampos = [...camposExtras, { 
      id: Date.now(), 
      descricao: novoCampoExtra.descricao, 
      valor: novoCampoExtra.valor,
      extenso: valorPorExtenso(novoCampoExtra.valor)
    }];
    onChange({ ...proposta, campos_extras: novosCampos });
    setNovoCampoExtra({ descricao: "", valor: 0 });
    setMesesDivisaoExtra("");
    toast.success("Campo adicionado!");
  };

  const handleUpdateCampoExtra = (id, field, value) => {
    const novosCampos = camposExtras.map(campo => {
      if (campo.id === id) {
        const atualizado = { ...campo, [field]: value };
        if (field === 'valor') {
          atualizado.extenso = valorPorExtenso(value);
        }
        return atualizado;
      }
      return campo;
    });
    onChange({ ...proposta, campos_extras: novosCampos });
  };

  const handleRemoveCampoExtra = (id) => {
    const novosCampos = camposExtras.filter(c => c.id !== id);
    onChange({ ...proposta, campos_extras: novosCampos });
  };

  const handleMoverCampoExtra = (index, direcao) => {
    const novosCampos = [...camposExtras];
    const novoIndex = index + direcao;
    if (novoIndex < 0 || novoIndex >= novosCampos.length) return;
    [novosCampos[index], novosCampos[novoIndex]] = [novosCampos[novoIndex], novosCampos[index]];
    onChange({ ...proposta, campos_extras: novosCampos });
  };

  const processarPlanilha = (file) => {
    console.log("Iniciando processamento da planilha:", file.name);
    
    const reader = new FileReader();
    
    reader.onload = (e) => {
      console.log("FileReader onload executado");
      const data = new Uint8Array(e.target.result);
      console.log("Dados lidos, tamanho:", data.length);
      
      const workbook = XLSX.read(data, { type: 'array' });
      console.log("Workbook lido, sheets:", workbook.SheetNames);
      
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "", raw: true });

      console.log("Dados brutos da planilha:", jsonData);

      if (!jsonData || jsonData.length === 0) {
        toast.error("Nenhum dado encontrado na planilha.");
        setImportando(false);
        setDialogImportacaoAberto(false);
        return;
      }

      // Pegar as colunas diretamente da planilha (cabeçalhos)
      const colunasOriginais = Object.keys(jsonData[0]);
      console.log("Colunas encontradas na planilha:", colunasOriginais);

      const parseNumero = (valor, tipoColuna = null) => {
        if (valor === null || valor === undefined || valor === "") return 0;
        
        // Se já é número do Excel, retornar diretamente
        if (typeof valor === 'number') {
          console.log(`parseNumero: valor numérico direto = ${valor}`);
          return valor;
        }
        
        // Limpar R$, espaços e converter formato brasileiro
        let str = String(valor).trim();
        str = str.replace(/R\$\s*/gi, '');
        str = str.replace(/\s/g, '');
        
        console.log(`parseNumero: string = "${str}", tipoColuna = ${tipoColuna}`);
        
        // Detectar formato brasileiro vs americano
        const ultimaVirgula = str.lastIndexOf(',');
        const ultimoPonto = str.lastIndexOf('.');
        
        // Contar quantos pontos e vírgulas existem
        const qtdPontos = (str.match(/\./g) || []).length;
        const qtdVirgulas = (str.match(/,/g) || []).length;
        
        if (ultimaVirgula > ultimoPonto) {
          // Formato brasileiro: vírgula é o separador decimal
          str = str.replace(/\./g, '').replace(',', '.');
        } else if (ultimoPonto > ultimaVirgula && ultimaVirgula !== -1) {
          // Formato americano com vírgula como milhar e ponto como decimal
          str = str.replace(/,/g, '');
        } else if (ultimaVirgula === -1 && ultimoPonto !== -1) {
          // Só tem ponto(s)
          if (qtdPontos > 1) {
            // Múltiplos pontos = separadores de milhar
            str = str.replace(/\./g, '');
          } else {
            // Um único ponto - para colunas de quantidade, SEMPRE é milhar
            if (tipoColuna === "numero") {
              str = str.replace('.', '');
            } else {
              // Para valor monetário, verificar contexto (str garantido string após String(valor))
              const partes = (str && typeof str === 'string' ? str : '').split('.');
              const digitosAposPonto = partes[1] ? partes[1].length : 0;
              if (digitosAposPonto === 3) {
                str = str.replace('.', '');
              }
            }
          }
        } else if (ultimoPonto === -1 && ultimaVirgula !== -1) {
          // Só tem vírgula(s)
          if (qtdVirgulas > 1) {
            str = str.replace(/,/g, '');
          } else {
            const partes = (str && typeof str === 'string' ? str : '').split(',');
            const digitosAposVirgula = partes[1] ? partes[1].length : 0;
            if (digitosAposVirgula === 3) {
              str = str.replace(',', '');
            } else {
              str = str.replace(',', '.');
            }
          }
        }
        
        const num = parseFloat(str);
        console.log(`parseNumero: resultado = ${num}`);
        return isNaN(num) ? 0 : num;
      };

      // Criar configuração de colunas baseada na planilha (APENAS as da planilha)
      const novasColunas = colunasOriginais.map((nomeColuna, index) => {
        const nomeLower = nomeColuna.toLowerCase().trim();
        
        // Detectar tipo baseado no nome - IMPORTANTE: qtd/quantidade tem prioridade sobre total/valor
        let tipo = "texto";
        if (nomeLower.includes("qtd") || nomeLower.includes("quantidade") || nomeLower.includes("quant")) {
          // Quantidade sempre é número, mesmo que tenha "total" no nome (ex: "QTD. TOTAL")
          tipo = "numero";
        } else if (nomeLower === "item" || nomeLower.includes("nº") || nomeLower.includes("num") || nomeLower.includes("seq")) {
          tipo = "numero";
        } else if (nomeLower.includes("valor") || nomeLower.includes("preço") || nomeLower.includes("preco") || nomeLower.includes("total") || nomeLower.includes("unit") || nomeLower.includes("r$")) {
          tipo = "valor";
        } else if (nomeLower.includes("desc") || nomeLower.includes("especific") || nomeLower.includes("objeto") || nomeLower.includes("serviço") || nomeLower.includes("produto") || nomeLower.includes("e-fisco")) {
          tipo = "texto_longo";
        }

        // Definir largura baseada no tipo - usar whitespace-nowrap para não truncar
        let largura = "whitespace-nowrap";
        if (tipo === "texto_longo") largura = "min-w-[350px]";
        else if (tipo === "valor") largura = "min-w-[130px] whitespace-nowrap";
        else if (tipo === "numero") largura = "min-w-[80px] whitespace-nowrap";

        // Primeira coluna (item) não é editável se for numérica
        const editavel = !(index === 0 && nomeLower === "item");

        return {
          id: `col_${index}`,
          nome: nomeColuna,
          tipo: tipo,
          largura: largura,
          visivel: true,
          editavel: editavel
        };
      });

      // Processar itens mantendo todas as colunas originais
      const itensProcessados = jsonData.map((row, index) => {
        const itemProcessado = { item: index + 1 };
        
        colunasOriginais.forEach((coluna, colIndex) => {
          const colunaId = `col_${colIndex}`;
          const valor = row[coluna];
          const colunaConfig = novasColunas[colIndex];
          
          if (colunaConfig?.tipo === "valor" || colunaConfig?.tipo === "numero") {
            itemProcessado[colunaId] = parseNumero(valor, colunaConfig.tipo);
          } else {
            itemProcessado[colunaId] = String(valor || "");
          }
        });

        console.log(`Item ${index + 1}:`, itemProcessado);
        return itemProcessado;
      }).filter((item) => {
        // Filtro permissivo - aceita linhas que tenham algum conteúdo significativo
        const valores = Object.entries(item).filter(([k]) => k !== 'item').map(([, v]) => v);
        return valores.some(v => (typeof v === 'string' && v.trim() !== "") || (typeof v === 'number' && v !== 0));
      });

      console.log("Itens processados:", itensProcessados);
      console.log("Novas colunas:", novasColunas);

      if (itensProcessados.length === 0) {
        toast.error("Nenhum item válido encontrado na planilha.");
        setImportando(false);
        setDialogImportacaoAberto(false);
        return;
      }

      // Calcular valor total - procurar última coluna de valor/total
      const colunasValor = novasColunas.filter(c => c.tipo === "valor");
      const ultimaColunaValor = colunasValor[colunasValor.length - 1];
      
      let valorTotalCalc = 0;
      if (ultimaColunaValor) {
        itensProcessados.forEach(item => {
          valorTotalCalc += item[ultimaColunaValor.id] || 0;
        });
      }

      console.log("Finalizando importação - atualizando estados");
      
      // Atualizar colunas locais PRIMEIRO
      setColunas(novasColunas);
      
      // Preparar proposta atualizada
      const propostaAtualizada = { 
        ...proposta, 
        itens: itensProcessados,
        colunas_itens: novasColunas,
        valor_total_proposta: valorTotalCalc
      };
      
      console.log("Proposta atualizada:", propostaAtualizada);
      
      console.log("Finalizando importação - salvando automaticamente");
      
      // Fechar dialogs
      setImportando(false);
      setDialogImportacaoAberto(false);
      
      // Atualizar estado local PRIMEIRO
      onChange(propostaAtualizada);
      onImportacaoPlanilhaConcluida?.();

      // Salvar automaticamente após um delay para garantir que o estado foi atualizado
      setTimeout(() => {
        if (onSaveImmediate) {
          console.log("Salvando automaticamente após importação");
          onSaveImmediate(propostaAtualizada)
            .then(() => {
              toast.success(`${itensProcessados.length} itens importados e salvos com sucesso!`);
            })
            .catch(error => {
              console.error("Erro no salvamento automático:", error);
              toast.warning(`${itensProcessados.length} itens importados! Clique em "Salvar Rascunho" para tentar novamente.`);
            });
        }
      }, 500);
      
    };

    reader.onerror = (error) => {
      console.error("Erro no FileReader:", error);
      toast.error("Erro ao ler o arquivo.");
      setImportando(false);
      setDialogImportacaoAberto(false);
    };

    console.log("Iniciando leitura do arquivo...");
    reader.readAsArrayBuffer(file);
  };

  const handleImportarPlanilha = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      console.log("Nenhum arquivo selecionado");
      return;
    }

    console.log("Arquivo selecionado:", file.name, file.type, file.size);
    
    setImportando(true);
    toast.info("Processando planilha...");

    // Processar diretamente
    processarPlanilha(file);
    
    // Limpar input depois de processar
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatarNumeroPt = (n) =>
    new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 6 }).format(n);

  const renderCelula = (item, coluna, index) => {
    const valor = item[coluna.id];

    if (coluna.tipo === "calculada") {
      const vals = aplicarColunasCalculadas(item, colunas);
      const v = extrairNumero(vals[coluna.id]);
      return <span className="font-mono text-slate-800">{formatarValor(v)}</span>;
    }

    if (coluna.tipo === 'numero') {
      return (
        <Input
          type="number"
          value={extrairNumero(valor) || ""}
          onChange={(e) => handleChangeItem(index, coluna.id, e.target.value)}
          className="text-center bg-white"
        />
      );
    }

    if (coluna.tipo === 'valor') {
      return (
        <CurrencyInput
          value={extrairNumero(valor)}
          onChange={(novoValor) => handleChangeItem(index, coluna.id, novoValor)}
          className="bg-white min-w-[148px] w-full"
        />
      );
    }

    if (coluna.tipo === 'texto_longo') {
      return (
        <Textarea
          value={valor || ""}
          onChange={(e) => handleChangeItem(index, coluna.id, e.target.value)}
          className="min-w-[300px] min-h-[100px] bg-white"
          placeholder="Digite a descrição..."
        />
      );
    }

    return (
      <Input
        value={valor || ""}
        onChange={(e) => handleChangeItem(index, coluna.id, e.target.value)}
        className="bg-white min-w-[150px]"
      />
    );
  };

  const colunasVisiveis = colunas.filter(c => c.visivel !== false);

  const classeAlinhamentoCabecalhoColuna = (coluna) =>
    coluna.tipo === "numero" || coluna.tipo === "valor" || coluna.tipo === "calculada"
      ? "text-right"
      : coluna.id === "item" || coluna.nome?.toLowerCase() === "item"
        ? "text-center"
        : "";

  const renderLinhaCabecalhoColunas = (keySuffix) => (
    <TableRow key={`hdr-${keySuffix}`} className="bg-muted/50 hover:bg-muted/50 border-b">
      {colunasVisiveis.map((coluna) => (
        <TableCell
          key={coluna.id}
          className={`h-10 px-2 align-middle font-medium text-muted-foreground ${classeLarguraCelulaItemProposta(coluna)} ${classeAlinhamentoCabecalhoColuna(coluna)}`}
        >
          {coluna.nome}
        </TableCell>
      ))}
      <TableCell className="h-10 w-16 px-2" />
    </TableRow>
  );

  const precisaCabecalhoInicial =
    itens.length > 0 && getTipoLinha(itens[0]) !== TIPO_LINHA.CABECALHO_GRUPO;

  return (
    <>
      <Card className="border-none shadow-xl bg-white/90 backdrop-blur-sm">
        <CardHeader className="border-b border-slate-100">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-amber-600" />
              Itens da Proposta
            </CardTitle>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-lg bg-slate-50">
                <Calculator className="w-4 h-4 text-slate-600" />
                <Label htmlFor="calculo-auto" className="text-sm cursor-pointer">
                  Cálculo Automático
                </Label>
                <Switch
                  id="calculo-auto"
                  checked={calculoAutomatico}
                  onCheckedChange={setCalculoAutomatico}
                />
              </div>
              <Button
                onClick={() => setDialogImportacaoAberto(true)}
                size="sm"
                variant="outline"
                className="border-green-300 text-green-700 hover:bg-green-50"
              >
                <Upload className="w-4 h-4 mr-2" />
                Importar Planilha
              </Button>
              <Button
                onClick={() => setDialogColunasAberto(true)}
                size="sm"
                variant="outline"
                className="border-blue-300 text-blue-700 hover:bg-blue-50"
              >
                <Settings className="w-4 h-4 mr-2" />
                Gerenciar Colunas
              </Button>
              <Button
                onClick={handleAddGrupo}
                size="sm"
                variant="outline"
                className="border-amber-300 text-amber-800 hover:bg-amber-50"
              >
                <Layers className="w-4 h-4 mr-2" />
                Grupo
              </Button>
              <Button
                onClick={handleAddSubtotal}
                size="sm"
                variant="outline"
                className="border-slate-400 text-slate-800 hover:bg-slate-100"
              >
                <Sigma className="w-4 h-4 mr-2" />
                Subtotal
              </Button>
              <Button
                onClick={handleAddItem}
                size="sm"
                className="bg-amber-600 hover:bg-amber-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Adicionar Item
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {itens.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <div>
                <p className="mb-4">Nenhum item adicionado</p>
                <Button onClick={handleAddItem} variant="outline">
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar Primeiro Item
                </Button>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableBody>
                  {precisaCabecalhoInicial ? renderLinhaCabecalhoColunas("inicial") : null}
                  {itens.map((item, index) => {
                    const tipoLinha = getTipoLinha(item);

                    if (tipoLinha === TIPO_LINHA.CABECALHO_GRUPO) {
                      return (
                        <React.Fragment key={`grupo-${index}`}>
                          <TableRow key={`grupo-titulo-${index}`} className="bg-amber-50/90 hover:bg-amber-50">
                            <TableCell colSpan={colunasVisiveis.length + 1} className="py-3">
                              <div className="flex items-center gap-3 pr-2">
                                <Layers className="w-4 h-4 text-amber-700 shrink-0" />
                                <Input
                                  value={item.titulo_grupo || ""}
                                  onChange={(e) => handleChangeTituloGrupo(index, e.target.value)}
                                  className="font-semibold bg-white border-amber-200 max-w-2xl"
                                  placeholder="Título do grupo (ex.: ITEM 1)"
                                />
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="ml-auto shrink-0 hover:bg-red-50 hover:text-red-600"
                                  onClick={() => handleRemoveItem(index)}
                                  title="Remover cabeçalho"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                          {renderLinhaCabecalhoColunas(`pos-${index}`)}
                        </React.Fragment>
                      );
                    }

                    if (tipoLinha === TIPO_LINHA.SUBTOTAL) {
                      return (
                        <TableRow key={index} className="bg-slate-100/90 font-semibold">
                          {colunasVisiveis.map((coluna, colIndex) => {
                            if (colIndex === 0) {
                              return (
                                <TableCell key={coluna.id} className="text-slate-800">
                                  Subtotal do grupo
                                </TableCell>
                              );
                            }
                            const isTexto =
                              coluna.tipo === "texto" || coluna.tipo === "texto_longo";
                            if (isTexto) {
                              return (
                                <TableCell
                                  key={coluna.id}
                                  className={
                                    coluna.tipo === "texto_longo" ? "align-top" : ""
                                  }
                                />
                              );
                            }
                            const soma = somarColunaAteSubtotal(itens, index, coluna, colunas);
                            return (
                              <TableCell
                                key={coluna.id}
                                className={
                                  coluna.tipo === "numero"
                                    ? "text-right font-mono"
                                    : "text-right font-mono text-slate-900"
                                }
                              >
                                {coluna.tipo === "numero"
                                  ? formatarNumeroPt(soma)
                                  : formatarValor(soma)}
                              </TableCell>
                            );
                          })}
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleRemoveItem(index)}
                              className="hover:bg-red-50 hover:text-red-600"
                              title="Remover subtotal"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    }

                    return (
                      <TableRow key={index}>
                        {colunasVisiveis.map((coluna) => (
                          <TableCell
                            key={coluna.id}
                            className={`${classeLarguraCelulaItemProposta(coluna)} ${
                              coluna.tipo === "numero" || coluna.tipo === "valor" || coluna.tipo === "calculada"
                                ? "text-right font-mono"
                                : coluna.id === "item" || coluna.nome?.toLowerCase() === "item"
                                  ? "font-medium text-center"
                                  : coluna.tipo === "texto_longo"
                                    ? "align-top"
                                    : ""
                            }`}
                          >
                            {coluna.tipo === "calculada" ? (
                              renderCelula(item, coluna, index)
                            ) : coluna.editavel !== false ? (
                              renderCelula(item, coluna, index)
                            ) : (
                              <span className="font-medium">{item[coluna.id]}</span>
                            )}
                          </TableCell>
                        ))}
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleRemoveItem(index)}
                            className="hover:bg-red-50 hover:text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
                
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-none shadow-xl bg-white/90 backdrop-blur-sm mt-6">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="flex items-center gap-2 text-base">
            <Sigma className="w-5 h-5 text-slate-800" />
            Valor total da proposta
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          <div className="space-y-2 max-w-md">
            <Label htmlFor="valor-total-proposta">Valor (R$)</Label>
            {calculoAutomatico ? (
              <div
                id="valor-total-proposta"
                className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-lg font-semibold tabular-nums text-slate-900"
              >
                {formatarValor(totais.totalGeral)}
              </div>
            ) : (
              <CurrencyInput
                id="valor-total-proposta"
                value={proposta.valor_total_proposta ?? 0}
                onChange={(val) => onChange({ ...proposta, valor_total_proposta: val })}
                className="bg-white"
              />
            )}
            {calculoAutomatico && (
              <p className="text-xs text-slate-500">
                Calculado automaticamente a partir dos itens. Desligue &quot;Cálculo automático&quot; acima para informar o valor manualmente.
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="texto-valor-total-proposta">Valor por extenso (texto no PDF)</Label>
            <Textarea
              id="texto-valor-total-proposta"
              value={proposta.texto_valor_total ?? ""}
              onChange={(e) =>
                onChange({
                  ...proposta,
                  texto_valor_total: e.target.value.trim() === "" ? null : e.target.value,
                })
              }
              placeholder='Ex.: Valor Global da Proposta de Preços R$ ...'
              rows={3}
              className="bg-white"
            />
          </div>
        </CardContent>
      </Card>

      {/* Card para Campos Extras (Descrição + Valor + Extenso) */}
      <Card className="border-none shadow-xl bg-white/90 backdrop-blur-sm mt-6">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="flex items-center gap-2 text-base">
            <Plus className="w-5 h-5 text-green-600" />
            Adicionar Campo Extra
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          {/* Formulário para adicionar novo campo */}
          <div className="grid grid-cols-1 gap-4 p-4 border border-dashed border-slate-300 rounded-lg bg-slate-50 xl:grid-cols-5 xl:items-end">
            <div className="space-y-2 xl:col-span-1">
              <Label className="font-bold">Descrição</Label>
              <Input
                value={novoCampoExtra.descricao}
                onChange={(e) => setNovoCampoExtra({ ...novoCampoExtra, descricao: e.target.value })}
                placeholder="Ex: Valor Global da Proposta"
                className="bg-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-bold">Quantidade de meses</Label>
              <Input
                type="number"
                min={1}
                step={1}
                value={mesesDivisaoExtra}
                onChange={(e) => setMesesDivisaoExtra(e.target.value)}
                placeholder="Ex: 12"
                className="bg-white"
              />
            </div>
            <div className="flex flex-col justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                className="h-10 w-full border-green-600 text-green-700 hover:bg-green-50"
                onClick={handleAplicarValorMensalExtra}
              >
                Aplicar valor mensal
              </Button>
            </div>
            <div className="space-y-2">
              <Label className="font-bold">R$</Label>
              <CurrencyInput
                value={novoCampoExtra.valor}
                onChange={(val) => setNovoCampoExtra({ ...novoCampoExtra, valor: val })}
                className="bg-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-bold">Valor por Extenso</Label>
              <div className="h-10 px-3 py-2 bg-slate-100 rounded-md border text-sm text-slate-700 overflow-hidden">
                {valorPorExtenso(novoCampoExtra.valor) || "-"}
              </div>
            </div>
          </div>
          <p className="text-xs text-slate-500">
            &quot;Aplicar valor mensal&quot; divide o valor total da proposta (mesmo exibido no topo) pela quantidade de meses e preenche o campo R$.
          </p>
          <div className="flex justify-center">
            <Button onClick={handleAddCampoExtra} className="bg-green-600 hover:bg-green-700">
              <Plus className="w-4 h-4 mr-2" />
              Adicionar Campo
            </Button>
          </div>

          {/* Lista de campos extras adicionados */}
          {camposExtras.length > 0 && (
            <div className="space-y-3 mt-6">
              <Label className="text-sm font-semibold text-slate-700">Campos Adicionados:</Label>
              {camposExtras.map((campo) => (
                <div key={campo.id} className="grid md:grid-cols-5 gap-4 p-4 border border-slate-200 rounded-lg bg-white items-end">
                  <div className="space-y-2">
                    <Label className="font-bold text-xs">Descrição</Label>
                    <Input
                      value={campo.descricao}
                      onChange={(e) => handleUpdateCampoExtra(campo.id, 'descricao', e.target.value)}
                      className="font-semibold"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-bold text-xs">R$</Label>
                    <CurrencyInput
                      value={campo.valor}
                      onChange={(val) => handleUpdateCampoExtra(campo.id, 'valor', val)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-bold text-xs">Valor por Extenso</Label>
                    <div className="h-10 px-3 py-2 bg-slate-50 rounded-md border text-sm text-slate-700 overflow-auto">
                      {campo.extenso || "-"}
                    </div>
                  </div>
                  <div className="flex gap-1 items-center">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleMoverCampoExtra(camposExtras.indexOf(campo), -1)}
                      disabled={camposExtras.indexOf(campo) === 0}
                      className="h-8 w-8"
                      title="Mover para cima"
                    >
                      <ArrowUp className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleMoverCampoExtra(camposExtras.indexOf(campo), 1)}
                      disabled={camposExtras.indexOf(campo) === camposExtras.length - 1}
                      className="h-8 w-8"
                      title="Mover para baixo"
                    >
                      <ArrowDown className="w-4 h-4" />
                    </Button>
                  </div>
                  <div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemoveCampoExtra(campo.id)}
                      className="hover:bg-red-50 hover:text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog para Importar Planilha */}
      <Dialog open={dialogImportacaoAberto} onOpenChange={setDialogImportacaoAberto}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-green-600" />
              Importar Planilha
            </DialogTitle>
            <DialogDescription>
              Importe uma planilha Excel (.xlsx, .xls) ou CSV com os itens da proposta.
              O sistema irá identificar automaticamente as colunas.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-green-400 transition-colors">
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleImportarPlanilha}
                disabled={importando}
                className="hidden"
                id="import-excel"
              />
              <label htmlFor="import-excel" className="cursor-pointer">
                {importando ? (
                  <div className="flex flex-col items-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600 mb-3"></div>
                    <p className="text-sm font-medium text-slate-700">Processando planilha...</p>
                    <p className="text-xs text-slate-500 mt-1">Isso pode levar alguns segundos</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <Upload className="w-10 h-10 text-slate-400 mb-3" />
                    <p className="text-sm font-medium text-slate-700">
                      Clique para selecionar a planilha
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      Formatos aceitos: .xlsx, .xls, .csv
                    </p>
                  </div>
                )}
              </label>
            </div>

            <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
              <h4 className="font-medium text-slate-900 mb-2">Colunas esperadas:</h4>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>• <strong>Item</strong> - Número do item</li>
                <li>• <strong>Descrição</strong> - Descrição do produto/serviço</li>
                <li>• <strong>Unidade</strong> - Unidade de medida (UN, HR, MÊS, etc)</li>
                <li>• <strong>Quantidade</strong> - Quantidade</li>
                <li>• <strong>Valor Unitário</strong> - Valor por unidade</li>
                <li>• <strong>Valor Total</strong> - Valor total do item (opcional)</li>
              </ul>
              <p className="text-xs text-slate-500 mt-3">
                * Colunas adicionais serão automaticamente identificadas e incluídas
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogImportacaoAberto(false)}
              disabled={importando}
            >
              Cancelar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialogColunasAberto} onOpenChange={setDialogColunasAberto}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Gerenciar Colunas dos Itens</DialogTitle>
            <DialogDescription>
              Adicione, remova, edite ou reordene as colunas da tabela de itens da proposta (arraste pelo ícone de alça para reordenar).
              Colunas do tipo &quot;Calculada&quot; usam fórmulas com {"{"}id_da_coluna{"}"} (ex.: {"{"}quantidade{"}"}*{"{"}valor_unitario{"}"}).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEndColunas}
            >
              <SortableContext
                items={colunas.map((c) => c.id)}
                strategy={verticalListSortingStrategy}
              >
                {colunas.map((coluna, index) => (
                  <ColunaSortableItem
                    key={coluna.id}
                    coluna={coluna}
                    index={index}
                    onChangeColuna={handleChangeColuna}
                    onRemoveColuna={handleRemoveColuna}
                    colunasLength={colunas.length}
                    colunaEditavel={coluna.editavel}
                  />
                ))}
              </SortableContext>
            </DndContext>

            <Button
              onClick={handleAddColuna}
              variant="outline"
              className="w-full border-dashed border-2"
            >
              <Plus className="w-4 h-4 mr-2" />
              Adicionar Nova Coluna
            </Button>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setColunas(proposta.colunas_itens || colunas);
                setDialogColunasAberto(false);
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSalvarColunas}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Salvar Colunas
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}